import { type PropsWithChildren, type ReactNode, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";

import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";

import { useOrganization } from "../hooks/useOrganization";
import { CommonPaths } from "../paths";

type OrganizationGuardProps = PropsWithChildren & {
  fallback?: ReactNode;
};

export const OrganizationGuard = ({ children }: OrganizationGuardProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasError, selectedOrgId, isLoading, isReady } = useOrganization();

  const whitelistedPaths = new Set([
    CommonPaths.Login,
    CommonPaths.NotFound,
    CommonPaths.OktaAuth,
    CommonPaths.SelectOrg,
    CommonPaths.Welcome,
  ]);

  useEffect(() => {
    if (
      isReady &&
      !selectedOrgId &&
      !whitelistedPaths.has(location.pathname as CommonPaths & string)
    ) {
      navigate(CommonPaths.SelectOrg);
    }
  }, [isReady, selectedOrgId, navigate, location.pathname]);

  if (whitelistedPaths.has(location.pathname as CommonPaths & string)) {
    return children;
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (hasError) {
    return (
      <Navigate replace state={{ from: location }} to={CommonPaths.SelectOrg} />
    );
  }

  if (!selectedOrgId) {
    return null;
  }

  return children;
};
